import type { Database } from 'sql.js';
import { lookupBonoNombreById } from '@core/infrastructure/driven/sqljs/sqlJsLookupBonoNombre.ts';
import { ensureBonoIdForPuntoEntregaCaptura } from '@core/infrastructure/driven/sqljs/sqlJsBonoFkFromCaption.ts';
import { participanteTipoBonoCatalogId as queryParticipanteTipoBonoCatalogId } from '@core/infrastructure/driven/sqljs/sqlJsLookupTipoBonoNombre.ts';
import type { RedencionBonoRepositoryPort } from '../../domain/ports/RedencionBonoRepositoryPort.ts';
import type {
  RedencionBonoHeader,
  RedencionBonoListItem,
  RedencionBonoParticipanteFormPayload,
  RedencionBonoCatalogSnapshot,
} from '../../domain/redencionBonoModel.ts';
import type { ParticipanteBusquedaRedencionPrefill } from '../../../shared/participante/domain/participanteLookupTypes.ts';
import type { ParticipanteBusquedaPrefillResolve } from '../../../shared/participante/domain/participanteBusquedaPrefillResolve.ts';
import { fetchRedencionBonoCatalog } from './sqlJsRedencionBono/catalogReads.ts';
import { deleteUploadedRedencionInDb } from './sqlJsRedencionBono/deleteUploadedRedencion.ts';
import {
  getRedencionById,
  listRedencionesBono,
  listRedencionesUploadedOfflineInDb,
} from './sqlJsRedencionBono/listRedenciones.ts';
import { countRedencionesPendingOfflineExport } from './sqlJsRedencionBono/countRedencionesPendingOfflineExport.ts';
import {
  findConflictingRedencionIdForParticipanteInDb,
  findRedencionIdForParticipanteInDb,
  listParticipantesForRedencionInDb,
} from './sqlJsRedencionBono/participantReads.ts';
import {
  resolveRedencionParticipantePrefillByDocumento,
  resolveRedencionParticipantePrefillLookup,
  resolveRedencionParticipantePrefillLookupByParticipanteId,
  listRedencionParticipanteBusquedaCandidates,
} from './redencionParticipantePrefillByDocumento.ts';
import {
  insertRedencionWithParticipantes,
  updateRedencionWithParticipantes,
} from './sqlJsRedencionBono/saveRedencionBono.ts';

/** SQLite (sql.js) implementation of {@link RedencionBonoRepositoryPort}. */
export class SqlJsRedencionBonoRepository implements RedencionBonoRepositoryPort {
  constructor(
    private readonly db: Database,
    private readonly onMutate: () => void,
    private readonly filesPublicPrefix: string = '',
  ) {}

  listRedenciones(): RedencionBonoListItem[] {
    return listRedencionesBono(this.db);
  }

  getRedencion(id: number): RedencionBonoListItem | null {
    return getRedencionById(this.db, id);
  }

  findRedencionIdForParticipante(participanteId: number): number | null {
    return findRedencionIdForParticipanteInDb(this.db, participanteId);
  }

  findConflictingRedencionIdForParticipante(
    participanteId: number,
    entregaCatalogId: number,
    proveedorCatalogId: number,
    excludedRedencionId: number | null,
  ): number | null {
    return findConflictingRedencionIdForParticipanteInDb(
      this.db,
      participanteId,
      entregaCatalogId,
      proveedorCatalogId,
      excludedRedencionId,
    );
  }

  listParticipantesForRedencion(redencionId: number): RedencionBonoParticipanteFormPayload[] {
    return listParticipantesForRedencionInDb(this.db, redencionId, this.filesPublicPrefix);
  }

  insertRedencion(h: RedencionBonoHeader, participantes: RedencionBonoParticipanteFormPayload[]): number {
    return insertRedencionWithParticipantes(this.db, h, participantes, this.onMutate);
  }

  updateRedencion(id: number, h: RedencionBonoHeader, participantes: RedencionBonoParticipanteFormPayload[]): void {
    updateRedencionWithParticipantes(this.db, id, h, participantes, this.onMutate);
  }

  getCatalog(): RedencionBonoCatalogSnapshot {
    return fetchRedencionBonoCatalog(this.db);
  }

  findParticipanteRedencionPrefillByDocumento(documento: string): ParticipanteBusquedaRedencionPrefill | null {
    return resolveRedencionParticipantePrefillByDocumento(this.db, this.filesPublicPrefix, documento);
  }

  resolveParticipanteRedencionBusquedaPrefill(
    documento: string,
  ): ParticipanteBusquedaPrefillResolve<ParticipanteBusquedaRedencionPrefill> {
    return resolveRedencionParticipantePrefillLookup(this.db, this.filesPublicPrefix, documento);
  }

  resolveParticipanteRedencionBusquedaPrefillByParticipanteId(
    participanteId: number,
  ): ParticipanteBusquedaPrefillResolve<ParticipanteBusquedaRedencionPrefill> {
    return resolveRedencionParticipantePrefillLookupByParticipanteId(
      this.db,
      this.filesPublicPrefix,
      participanteId,
    );
  }

  listParticipanteBusquedaCandidates(documentoONombre: string) {
    return listRedencionParticipanteBusquedaCandidates(this.db, documentoONombre);
  }

  getBonoNombreById(bonoId: number): string | null {
    if (!Number.isFinite(bonoId) || bonoId <= 0) return null;
    return lookupBonoNombreById(this.db, bonoId);
  }

  resolveBonoIdForRedencionCaptura(caption: string, tipoBonoCatalogId: number): number | null {
    const r = ensureBonoIdForPuntoEntregaCaptura(this.db, caption, tipoBonoCatalogId);
    if (r == null) return null;
    if (r.inserted) this.onMutate();
    return r.bonoId;
  }

  participanteTipoBonoCatalogId(participanteId: number): number | null {
    return queryParticipanteTipoBonoCatalogId(this.db, participanteId);
  }

  listRedencionesUploadedOffline(): RedencionBonoListItem[] {
    return listRedencionesUploadedOfflineInDb(this.db);
  }

  countPendingOfflineExport(): number {
    return countRedencionesPendingOfflineExport(this.db);
  }

  deleteUploadedRedencion(id: number): void {
    deleteUploadedRedencionInDb(this.db, this.onMutate, id);
  }
}
